import React from 'react';
import { formatDate } from '../../utils/helpers';
import { FiEdit2, FiTrash2, FiCheckCircle, FiRotateCcw, FiAlertCircle, FiUser } from 'react-icons/fi';
import { INCIDENT_STATUS, USER_ROLES } from '../../utils/constants';
import clsx from 'clsx';

const IncidentTable = ({
  incidents,
  onEdit,
  onDelete,
  onClose,
  onReopen,
  currentUser,
  loading
}) => {
  const canEdit = (incident) => {
    return currentUser?.role === USER_ROLES.ESTABLISHMENT_ADMIN ||
           incident.created_by === currentUser?.id;
  };

  const canClose = () => {
    return currentUser?.role === USER_ROLES.ESTABLISHMENT_ADMIN;
  };

  const canReopen = () => {
    return currentUser?.role === USER_ROLES.ESTABLISHMENT_ADMIN;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 mt-4">Cargando incidencias...</p>
      </div>
    );
  }

  if (incidents.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <FiAlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No se encontraron incidencias
        </h3>
        <p className="text-gray-500">
          No hay incidencias que coincidan con los filtros actuales
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Habitación
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Creado por
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cerrado por
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {incidents.map((incident) => (
              <tr key={incident.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {incident.room_number}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate">
                    {incident.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={clsx(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    {
                      'bg-red-100 text-red-800': incident.status === INCIDENT_STATUS.OPEN,
                      'bg-green-100 text-green-800': incident.status === INCIDENT_STATUS.CLOSED,
                    }
                  )}>
                    {incident.status === INCIDENT_STATUS.OPEN ? 'Abierta' : 'Cerrada'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <FiUser className="h-4 w-4 text-white" />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {incident.creator_name || 'Usuario desconocido'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {incident.status === INCIDENT_STATUS.CLOSED && incident.closer_name ? (
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center">
                        <FiUser className="h-4 w-4 text-white" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {incident.closer_name}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                    {formatDate(new Date(incident.created_at))}
                  </div>
                  {incident.status === INCIDENT_STATUS.CLOSED && incident.closed_at && (
                    <div className="text-xs text-gray-400">
                      Cerrada: {formatDate(new Date(incident.closed_at))}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    {canEdit(incident) && (
                      <button
                        onClick={() => onEdit(incident)}
                        className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                        title="Editar incidencia"
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                    )}

                    {incident.status === INCIDENT_STATUS.OPEN && canClose() && (
                      <button
                        onClick={() => onClose(incident.id)}
                        className="text-green-600 hover:text-green-800 transition-colors p-1"
                        title="Cerrar incidencia"
                      >
                        <FiCheckCircle className="h-4 w-4" />
                      </button>
                    )}

                    {incident.status === INCIDENT_STATUS.CLOSED && canReopen() && (
                      <button
                        onClick={() => onReopen(incident.id)}
                        className="text-yellow-600 hover:text-yellow-800 transition-colors p-1"
                        title="Reabrir incidencia"
                      >
                        <FiRotateCcw className="h-4 w-4" />
                      </button>
                    )}

                    {canEdit(incident) && (
                      <button
                        onClick={() => onDelete(incident)}
                        className="text-red-600 hover:text-red-800 transition-colors p-1"
                        title="Eliminar incidencia"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncidentTable;